import React, { useRef, useState, useEffect } from 'react';
import { ResourceManager } from './components/ResourceManager';
import ImageUploadGallery from '@/components/ImageUploadGallery';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';
import { supabase } from '@/lib/supabaseClient';
import { Download, Upload } from 'lucide-react';

const TeamModule = () => {
  const { records, refresh } = useSupabaseResource('team');
  const [uploading, setUploading] = useState(false);
  const [roles, setRoles] = useState([]);
  const fileInputRef = useRef();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    const { data, error } = await supabase
      .from('team_roles')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      setRoles(data || []);
    }
  };

  const exportToCSV = () => {
    const headers = ['name', 'role_id', 'image', 'bio', 'display_order', 'is_active'];
    const csvContent = [
      headers.join(','),
      ...records.map(record => headers.map(h => {
        if (h === 'role_id') {
          const role = roles.find(r => r.id === record[h]);
          return `"${role ? role.name : record[h] || ''}"`;
        }
        return `"${record[h] || ''}"`;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        alert('CSV must have headers and at least one row');
        return;
      }
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const requiredHeaders = ['name', 'role'];
      const missing = requiredHeaders.filter(h => !headers.includes(h));
      if (missing.length) {
        alert(`Missing required headers: ${missing.join(', ')}`);
        return;
      }
      const team = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.replace(/"/g, '').trim());
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i] || '';
        });
        // map role name to id
        const role = roles.find(r => r.name.toLowerCase() === obj.role.toLowerCase());
        obj.role_id = role ? role.id : obj.role;
        delete obj.role; // remove the old role field
        // transform
        obj.display_order = Number(obj.display_order) || 0;
        obj.is_active = obj.is_active !== 'false';
        return obj;
      });
      const { error } = await supabase.from('team').insert(team);
      if (error) throw error;
      alert('Bulk upload successful');
      refresh();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <ResourceManager
        table="team"
        title="Team Members"
        description="Manage team members displayed on the public team page."
        columns={[
          {
            label: 'Image',
            accessor: 'image',
            render: (value) => value ? (
              <img src={value} alt="Team member" className="w-12 h-12 object-cover rounded" />
            ) : 'No image'
          },
          { label: 'Name', accessor: 'name' },
          {
            label: 'Role',
            accessor: 'role_id',
            render: (value) => {
              const role = roles.find(r => r.id === value);
              return role ? role.name : 'Unknown';
            }
          },
          { label: 'Order', accessor: 'display_order' },
          {
            label: 'Active',
            accessor: 'is_active',
            render: (value) => value ? 'Yes' : 'No'
          },
        ]}
        formFields={[
          { name: 'name', label: 'Name', placeholder: 'John Doe' },
          { name: 'role_id', label: 'Role', type: 'select', options: roles.map(role => ({
            label: role.name,
            value: role.id
          }))},
          { name: 'images', label: 'Profile Image', type: 'custom', component: ImageUploadGallery },
          { name: 'bio', label: 'Bio', type: 'textarea', rows: 4 },
          { name: 'display_order', label: 'Display Order', type: 'number', placeholder: '0' },
          { name: 'is_active', label: 'Active', type: 'checkbox' },
        ]}
        defaultValues={{
          name: '',
          role_id: roles.length > 0 ? roles[0].id : '',
          images: [],
          bio: '',
          display_order: 0,
          is_active: true,
        }}
        transformBeforeSave={(payload) => {
          const images = Array.isArray(payload.images) ? payload.images : [];
          const featuredImage = images.length > 0 ? images[0] : '';
          const displayOrder = Number(payload.display_order);
          const roleId = payload.role_id || null;

          if (!roleId) {
            throw new Error('Please select a role before saving.');
          }

          return {
            ...payload,
            role_id: roleId,
            display_order: Number.isFinite(displayOrder) ? displayOrder : 0,
            is_active: Boolean(payload.is_active),
            image: featuredImage,
            images,
          };
        }}
        handleEdit={(record) => {
          // Ensure images is an array for existing records
          const images = record.images
            ? (Array.isArray(record.images) ? record.images : [record.images])
            : (record.image ? [record.image] : []);

          return {
            ...record,
            images,
          };
        }}
        extraButtons={[
          { label: 'Export CSV', onClick: exportToCSV, icon: Download },
          { label: uploading ? 'Uploading...' : 'Bulk Upload', onClick: () => fileInputRef.current.click(), disabled: uploading, icon: Upload },
        ]}
      />
    </>
  );
};

export default TeamModule;
